interface SubscriptionIssue {
  issueNumber: number;
  supplyDate: string;
  amount: number;
  paymentStatus: "paid" | "pending" | "failed";
  paymentDate?: string;
  paymentMethod?: string;
  notes?: string;
}

export interface Subscription {
  id: string;
  patientName: string;
  patientInitials: string;
  patientNHS: string;
  medication: string;
  prescribedBy: string;
  currentIssue: number;
  totalIssues: number;
  lastSupplyDate: string; // ISO date string
  nextSupplyDate: string; // ISO date string
  repeatPeriod: string; // e.g., "Every month", "Every 2 weeks"
  status: "awaiting_payment" | "overdue" | "supplied" | "consultation_required";
  serviceType: string;
  consultationDate: string;
  consultationTime: string;
  dosage: string;
  quantity: string;
  priority: "normal" | "urgent";
  pricePerIssue: number;
  issueHistory: SubscriptionIssue[];
}

export const subscriptionsData: Subscription[] = [
  {
    id: "sub-001",
    patientName: "Sarah Wilson",
    patientInitials: "SW",
    patientNHS: "NHS123456789",
    medication: "Metformin 500mg",
    prescribedBy: "Dr. Emily Carter",
    currentIssue: 3,
    totalIssues: 12,
    lastSupplyDate: "2024-12-15T00:00:00Z",
    nextSupplyDate: "2025-01-15T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2024-10-15",
    consultationTime: "09:30",
    dosage: "500mg twice daily",
    quantity: "56 tablets",
    priority: "normal",
    pricePerIssue: 12.5,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-10-15T00:00:00Z",
        amount: 12.5,
        paymentStatus: "paid",
        paymentDate: "2024-10-14T00:00:00Z",
        paymentMethod: "Card ending 4321",
        notes: "Initial prescription supply",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-11-15T00:00:00Z",
        amount: 12.5,
        paymentStatus: "paid",
        paymentDate: "2024-11-14T00:00:00Z",
        paymentMethod: "Card ending 4321",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-12-15T00:00:00Z",
        amount: 12.5,
        paymentStatus: "paid",
        paymentDate: "2024-12-14T00:00:00Z",
        paymentMethod: "Card ending 4321",
      },
    ],
  },
  {
    id: "sub-002",
    patientName: "James Rodriguez",
    patientInitials: "JR",
    patientNHS: "NHS987654321",
    medication: "Lisinopril 10mg",
    prescribedBy: "Dr. Michael Thompson",
    currentIssue: 6,
    totalIssues: 12,
    lastSupplyDate: "2024-12-01T00:00:00Z",
    nextSupplyDate: "2025-01-01T00:00:00Z",
    repeatPeriod: "Every month",
    status: "overdue",
    serviceType: "general-consultation",
    consultationDate: "2024-06-01",
    consultationTime: "14:15",
    dosage: "10mg once daily",
    quantity: "28 tablets",
    priority: "urgent",
    pricePerIssue: 15.75,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-06-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "paid",
        paymentDate: "2024-05-31T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-07-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "paid",
        paymentDate: "2024-06-30T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-08-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "paid",
        paymentDate: "2024-07-31T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-09-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "paid",
        paymentDate: "2024-08-31T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-10-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "paid",
        paymentDate: "2024-09-30T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-12-01T00:00:00Z",
        amount: 15.75,
        paymentStatus: "pending",
        notes: "Payment overdue - direct debit failed",
      },
    ],
  },
  {
    id: "sub-003",
    patientName: "Maria Garcia",
    patientInitials: "MG",
    patientNHS: "NHS456789123",
    medication: "Atorvastatin 20mg",
    prescribedBy: "Dr. Sarah Johnson",
    currentIssue: 2,
    totalIssues: 6,
    lastSupplyDate: "2024-11-20T00:00:00Z",
    nextSupplyDate: "2024-12-20T00:00:00Z",
    repeatPeriod: "Every month",
    status: "awaiting_payment",
    serviceType: "pharmacy-first",
    consultationDate: "2024-09-20",
    consultationTime: "11:45",
    dosage: "20mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 18.99,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-09-20T00:00:00Z",
        amount: 18.99,
        paymentStatus: "paid",
        paymentDate: "2024-09-19T00:00:00Z",
        paymentMethod: "Card ending 9876",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-11-20T00:00:00Z",
        amount: 18.99,
        paymentStatus: "pending",
        notes: "Awaiting payment authorisation",
      },
    ],
  },
  {
    id: "sub-004",
    patientName: "Robert Chen",
    patientInitials: "RC",
    patientNHS: "NHS789123456",
    medication: "Omeprazole 20mg",
    prescribedBy: "Dr. David Lee",
    currentIssue: 8,
    totalIssues: 12,
    lastSupplyDate: "2024-12-10T00:00:00Z",
    nextSupplyDate: "2024-12-24T00:00:00Z",
    repeatPeriod: "Every 2 weeks",
    status: "supplied",
    serviceType: "minor-ailment",
    consultationDate: "2024-04-10",
    consultationTime: "16:30",
    dosage: "20mg once daily",
    quantity: "14 capsules",
    priority: "normal",
    pricePerIssue: 8.45,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-04-10T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-04-09T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-04-24T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-04-23T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-05-08T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-05-07T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-05-22T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-05-21T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-06-05T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-06-04T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-06-19T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-06-18T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 7,
        supplyDate: "2024-11-26T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-11-25T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
      {
        issueNumber: 8,
        supplyDate: "2024-12-10T00:00:00Z",
        amount: 8.45,
        paymentStatus: "paid",
        paymentDate: "2024-12-09T00:00:00Z",
        paymentMethod: "Card ending 1234",
      },
    ],
  },
  {
    id: "sub-005",
    patientName: "Linda Thompson",
    patientInitials: "LT",
    patientNHS: "NHS321654987",
    medication: "Levothyroxine 100mcg",
    prescribedBy: "Dr. Amanda White",
    currentIssue: 4,
    totalIssues: 12,
    lastSupplyDate: "2024-12-05T00:00:00Z",
    nextSupplyDate: "2025-01-05T00:00:00Z",
    repeatPeriod: "Every month",
    status: "awaiting_payment",
    serviceType: "general-consultation",
    consultationDate: "2024-08-05",
    consultationTime: "10:00",
    dosage: "100mcg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 9.25,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-08-05T00:00:00Z",
        amount: 9.25,
        paymentStatus: "paid",
        paymentDate: "2024-08-04T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-09-05T00:00:00Z",
        amount: 9.25,
        paymentStatus: "paid",
        paymentDate: "2024-09-04T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-10-05T00:00:00Z",
        amount: 9.25,
        paymentStatus: "paid",
        paymentDate: "2024-10-04T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-12-05T00:00:00Z",
        amount: 9.25,
        paymentStatus: "pending",
      },
    ],
  },
  {
    id: "sub-006",
    patientName: "Kevin O'Connor",
    patientInitials: "KO",
    patientNHS: "NHS654987321",
    medication: "Amlodipine 5mg",
    prescribedBy: "Dr. Rebecca Martin",
    currentIssue: 1,
    totalIssues: 6,
    lastSupplyDate: "2024-12-18T00:00:00Z",
    nextSupplyDate: "2025-01-18T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2024-11-18",
    consultationTime: "13:20",
    dosage: "5mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 13.8,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-12-18T00:00:00Z",
        amount: 13.8,
        paymentStatus: "paid",
        paymentDate: "2024-12-17T00:00:00Z",
        paymentMethod: "Card ending 7890",
      },
    ],
  },
  {
    id: "sub-007",
    patientName: "Patricia Davis",
    patientInitials: "PD",
    patientNHS: "NHS147258369",
    medication: "Sertraline 50mg",
    prescribedBy: "Dr. John Wilson",
    currentIssue: 9,
    totalIssues: 12,
    lastSupplyDate: "2024-11-25T00:00:00Z",
    nextSupplyDate: "2024-12-25T00:00:00Z",
    repeatPeriod: "Every month",
    status: "overdue",
    serviceType: "general-consultation",
    consultationDate: "2024-02-25",
    consultationTime: "15:45",
    dosage: "50mg once daily",
    quantity: "28 tablets",
    priority: "urgent",
    pricePerIssue: 22.5,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-02-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-02-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-03-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-03-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-04-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-04-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-05-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-05-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-06-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-06-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-07-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-07-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 7,
        supplyDate: "2024-08-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-08-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 8,
        supplyDate: "2024-09-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "paid",
        paymentDate: "2024-09-24T00:00:00Z",
        paymentMethod: "Card ending 5555",
      },
      {
        issueNumber: 9,
        supplyDate: "2024-11-25T00:00:00Z",
        amount: 22.5,
        paymentStatus: "failed",
        notes: "Payment failed - card expired",
      },
    ],
  },
  {
    id: "sub-008",
    patientName: "Michael Brown",
    patientInitials: "MB",
    patientNHS: "NHS258369147",
    medication: "Simvastatin 40mg",
    prescribedBy: "Dr. Lisa Anderson",
    currentIssue: 5,
    totalIssues: 12,
    lastSupplyDate: "2024-12-12T00:00:00Z",
    nextSupplyDate: "2025-01-12T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2024-07-12",
    consultationTime: "09:15",
    dosage: "40mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 11.2,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-07-12T00:00:00Z",
        amount: 11.2,
        paymentStatus: "paid",
        paymentDate: "2024-07-11T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-08-12T00:00:00Z",
        amount: 11.2,
        paymentStatus: "paid",
        paymentDate: "2024-08-11T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-09-12T00:00:00Z",
        amount: 11.2,
        paymentStatus: "paid",
        paymentDate: "2024-09-11T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-10-12T00:00:00Z",
        amount: 11.2,
        paymentStatus: "paid",
        paymentDate: "2024-10-11T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-12-12T00:00:00Z",
        amount: 11.2,
        paymentStatus: "paid",
        paymentDate: "2024-12-11T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
    ],
  },
  {
    id: "sub-009",
    patientName: "Jennifer Wilson",
    patientInitials: "JW",
    patientNHS: "NHS369147258",
    medication: "Warfarin 5mg",
    prescribedBy: "Dr. Thomas Wright",
    currentIssue: 6,
    totalIssues: 6,
    lastSupplyDate: "2024-12-01T00:00:00Z",
    nextSupplyDate: "2025-01-01T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "general-consultation",
    consultationDate: "2024-06-01",
    consultationTime: "11:30",
    dosage: "5mg once daily",
    quantity: "28 tablets",
    priority: "urgent",
    pricePerIssue: 35.99,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-06-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-05-31T00:00:00Z",
        paymentMethod: "Card ending 2468",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-07-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-06-30T00:00:00Z",
        paymentMethod: "Card ending 2468",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-08-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-07-31T00:00:00Z",
        paymentMethod: "Card ending 2468",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-09-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-08-31T00:00:00Z",
        paymentMethod: "Card ending 2468",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-10-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-09-30T00:00:00Z",
        paymentMethod: "Card ending 2468",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-12-01T00:00:00Z",
        amount: 35.99,
        paymentStatus: "paid",
        paymentDate: "2024-11-30T00:00:00Z",
        paymentMethod: "Card ending 2468",
        notes: "Final issue - consultation required for renewal",
      },
    ],
  },
  {
    id: "sub-010",
    patientName: "Daniel Martinez",
    patientInitials: "DM",
    patientNHS: "NHS471582693",
    medication: "Prednisolone 10mg",
    prescribedBy: "Dr. Helen Clarke",
    currentIssue: 12,
    totalIssues: 12,
    lastSupplyDate: "2024-11-28T00:00:00Z",
    nextSupplyDate: "2024-12-28T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2023-11-28",
    consultationTime: "14:45",
    dosage: "10mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 14.65,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2023-11-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2023-11-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2023-12-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2023-12-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-01-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-01-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-02-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-02-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-03-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-03-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-04-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-04-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 7,
        supplyDate: "2024-05-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-05-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 8,
        supplyDate: "2024-06-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-06-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 9,
        supplyDate: "2024-07-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-07-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 10,
        supplyDate: "2024-08-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-08-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 11,
        supplyDate: "2024-09-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-09-27T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 12,
        supplyDate: "2024-11-28T00:00:00Z",
        amount: 14.65,
        paymentStatus: "paid",
        paymentDate: "2024-11-27T00:00:00Z",
        paymentMethod: "Direct Debit",
        notes: "Course completed - consultation required for renewal",
      },
    ],
  },
  ,
  {
    id: "sub-011",
    patientName: "Olivia Harris",
    patientInitials: "OH",
    patientNHS: "NHS582741963",
    medication: "Losartan 50mg",
    prescribedBy: "Dr. Peter Collins",
    currentIssue: 2,
    totalIssues: 12,
    lastSupplyDate: "2024-12-08T00:00:00Z",
    nextSupplyDate: "2025-01-08T00:00:00Z",
    repeatPeriod: "Every month",
    status: "awaiting_payment",
    serviceType: "general-consultation",
    consultationDate: "2024-10-08",
    consultationTime: "10:45",
    dosage: "50mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 16.4,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-10-08T00:00:00Z",
        amount: 16.4,
        paymentStatus: "paid",
        paymentDate: "2024-10-07T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-12-08T00:00:00Z",
        amount: 16.4,
        paymentStatus: "pending",
      },
    ],
  },
  {
    id: "sub-012",
    patientName: "Ethan Walker",
    patientInitials: "EW",
    patientNHS: "NHS741963852",
    medication: "Clopidogrel 75mg",
    prescribedBy: "Dr. Naomi Patel",
    currentIssue: 7,
    totalIssues: 12,
    lastSupplyDate: "2024-12-05T00:00:00Z",
    nextSupplyDate: "2025-01-05T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2024-06-05",
    consultationTime: "12:10",
    dosage: "75mg once daily",
    quantity: "28 tablets",
    priority: "normal",
    pricePerIssue: 19.9,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-06-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-06-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-07-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-07-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-08-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-08-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-09-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-09-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-10-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-10-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 6,
        supplyDate: "2024-11-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-11-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
      {
        issueNumber: 7,
        supplyDate: "2024-12-05T00:00:00Z",
        amount: 19.9,
        paymentStatus: "paid",
        paymentDate: "2024-12-04T00:00:00Z",
        paymentMethod: "Card ending 3141",
      },
    ],
  },
  {
    id: "sub-013",
    patientName: "Sophia Turner",
    patientInitials: "ST",
    patientNHS: "NHS963852741",
    medication: "Hydrochlorothiazide 25mg",
    prescribedBy: "Dr. Omar Rahman",
    currentIssue: 4,
    totalIssues: 12,
    lastSupplyDate: "2024-11-10T00:00:00Z",
    nextSupplyDate: "2024-12-10T00:00:00Z",
    repeatPeriod: "Every month",
    status: "overdue",
    serviceType: "general-consultation",
    consultationDate: "2024-07-10",
    consultationTime: "09:20",
    dosage: "25mg once daily",
    quantity: "28 tablets",
    priority: "urgent",
    pricePerIssue: 7.8,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-07-10T00:00:00Z",
        amount: 7.8,
        paymentStatus: "paid",
        paymentDate: "2024-07-09T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-08-10T00:00:00Z",
        amount: 7.8,
        paymentStatus: "paid",
        paymentDate: "2024-08-09T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-09-10T00:00:00Z",
        amount: 7.8,
        paymentStatus: "paid",
        paymentDate: "2024-09-09T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 4,
        supplyDate: "2024-11-10T00:00:00Z",
        amount: 7.8,
        paymentStatus: "pending",
      },
    ],
  },
  {
    id: "sub-014",
    patientName: "Liam Scott",
    patientInitials: "LS",
    patientNHS: "NHS852741369",
    medication: "Amoxicillin 500mg",
    prescribedBy: "Dr. Rachel Evans",
    currentIssue: 2,
    totalIssues: 3,
    lastSupplyDate: "2024-12-22T00:00:00Z",
    nextSupplyDate: "2025-01-22T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "minor-ailment",
    consultationDate: "2024-12-01",
    consultationTime: "08:50",
    dosage: "500mg three times daily",
    quantity: "21 capsules",
    priority: "normal",
    pricePerIssue: 6.2,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-12-01T00:00:00Z",
        amount: 6.2,
        paymentStatus: "paid",
        paymentDate: "2024-11-30T00:00:00Z",
        paymentMethod: "Card ending 9021",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-12-22T00:00:00Z",
        amount: 6.2,
        paymentStatus: "paid",
        paymentDate: "2024-12-21T00:00:00Z",
        paymentMethod: "Card ending 9021",
      },
    ],
  },
  {
    id: "sub-015",
    patientName: "Ava Lewis",
    patientInitials: "AL",
    patientNHS: "NHS159753486",
    medication: "Metoprolol 50mg",
    prescribedBy: "Dr. Henry Moore",
    currentIssue: 3,
    totalIssues: 12,
    lastSupplyDate: "2024-11-30T00:00:00Z",
    nextSupplyDate: "2024-12-30T00:00:00Z",
    repeatPeriod: "Every month",
    status: "awaiting_payment",
    serviceType: "general-consultation",
    consultationDate: "2024-09-30",
    consultationTime: "16:05",
    dosage: "50mg twice daily",
    quantity: "56 tablets",
    priority: "normal",
    pricePerIssue: 12.1,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-09-30T00:00:00Z",
        amount: 12.1,
        paymentStatus: "paid",
        paymentDate: "2024-09-29T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 2,
        supplyDate: "2024-10-30T00:00:00Z",
        amount: 12.1,
        paymentStatus: "paid",
        paymentDate: "2024-10-29T00:00:00Z",
        paymentMethod: "Direct Debit",
      },
      {
        issueNumber: 3,
        supplyDate: "2024-11-30T00:00:00Z",
        amount: 12.1,
        paymentStatus: "pending",
      },
    ],
  },
  {
    id: "sub-016",
    patientName: "Noah King",
    patientInitials: "NK",
    patientNHS: "NHS258147369",
    medication: "Apixaban 5mg",
    prescribedBy: "Dr. Victoria Hughes",
    currentIssue: 10,
    totalIssues: 12,
    lastSupplyDate: "2024-12-11T00:00:00Z",
    nextSupplyDate: "2025-01-11T00:00:00Z",
    repeatPeriod: "Every month",
    status: "supplied",
    serviceType: "pharmacy-first",
    consultationDate: "2024-03-11",
    consultationTime: "11:25",
    dosage: "5mg twice daily",
    quantity: "56 tablets",
    priority: "normal",
    pricePerIssue: 29.5,
    issueHistory: [
      {
        issueNumber: 1,
        supplyDate: "2024-03-11T00:00:00Z",
        amount: 29.5,
        paymentStatus: "paid",
        paymentDate: "2024-03-10T00:00:00Z",
        paymentMethod: "Card ending 5511",
      },
      {
        issueNumber: 5,
        supplyDate: "2024-07-11T00:00:00Z",
        amount: 29.5,
        paymentStatus: "paid",
      },
      {
        issueNumber: 10,
        supplyDate: "2024-12-11T00:00:00Z",
        amount: 29.5,
        paymentStatus: "paid",
      },
    ],
  },
];

// Helper functions for date calculations
export const getDaysAgo = (dateString: string): number => {
  const targetDate = new Date(dateString);
  const currentDate = new Date();
  const diffInTime = currentDate.getTime() - targetDate.getTime();
  const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
  return diffInDays;
};

export const getDaysUntil = (dateString: string): number => {
  const targetDate = new Date(dateString);
  const currentDate = new Date();
  const diffInTime = targetDate.getTime() - currentDate.getTime();
  const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
  return diffInDays;
};

export const getStatusColor = (status: Subscription["status"]): string => {
  switch (status) {
    case "awaiting_payment":
      return "bg-yellow-100 text-yellow-800";
    case "overdue":
      return "bg-red-100 text-red-800";
    case "supplied":
      return "bg-green-100 text-green-800";
    case "consultation_required":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const getStatusLabel = (status: Subscription["status"]): string => {
  switch (status) {
    case "awaiting_payment":
      return "Awaiting Payment";
    case "overdue":
      return "Overdue";
    case "supplied":
      return "Supplied";
    case "consultation_required":
      return "Consultation Required";
    default:
      return status;
  }
};

// Get the effective status based on subscription progress
export const getEffectiveStatus = (
  subscription: Subscription,
): Subscription["status"] => {
  // If all issues have been completed, status should be consultation_required
  if (subscription.currentIssue >= subscription.totalIssues) {
    return "consultation_required";
  }
  // Otherwise, return the original status
  return subscription.status;
};
